
import React, { useEffect, useState, useRef, useCallback } from 'react';
import useCalls from '../hooks/useCalls';
import { useUser } from '../hooks/useUser';
import CallsTable from '../components/calls/CallsTable';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import DateRangePicker from '../components/common/DateRangePicker';
import { USER_SENTIMENT } from '../utils/constants';
import { formatDate } from '../utils/formatters';
import { useSettings } from '../contexts/SettingsContext';

/**
 * CallsPage component
 */
const CallsPage = () => {
  const { calls, isLoading, error, fetchCalls, loadMore, hasMore } = useCalls();
  const { groupId } = useUser();
  const { selectedLocationId } = useSettings();
  
  // Filter state
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [dateRange, setDateRange] = useState({ start: null, end: null, preset: 'all_time' });
  const [sentimentFilter, setSentimentFilter] = useState([]);
  const [successFilter, setSuccessFilter] = useState(null); // null, true, or false
  const [showFilters, setShowFilters] = useState(false);
  
  const datePickerRef = useRef(null);

  /**
   * Build filter criteria based on current filters
   */
  const buildFilterCriteria = useCallback(() => {
    const criteria = {
      call_status: ['ended'], // Only show ended calls
    };

    // Date range filter
    if (dateRange.start && dateRange.end) {
      // Set start to beginning of the day (00:00:00)
      const startTime = new Date(dateRange.start);
      startTime.setHours(0, 0, 0, 0);
      
      // Set end to end of the day (23:59:59.999)
      const endTime = new Date(dateRange.end);
      endTime.setHours(23, 59, 59, 999);
      
      criteria.start_timestamp = {
        lower_threshold: startTime.getTime(),
        upper_threshold: endTime.getTime(),
      };
    } else if (dateRange.preset !== 'all_time') {
      // Apply preset date range
      const range = getDateRangeFromPreset(dateRange.preset);
      if (range) {
        criteria.start_timestamp = {
          lower_threshold: range.start,
          upper_threshold: range.end,
        };
      }
    }

    // Sentiment filter
    if (sentimentFilter.length > 0) {
      criteria.user_sentiment = sentimentFilter;
    }

    // Success filter
    if (successFilter !== null) {
      criteria.call_successful = [successFilter];
    }

    return criteria;
  }, [dateRange, sentimentFilter, successFilter]);

  /**
   * Fetch calls on mount and when filters change
   */
  useEffect(() => {
    const filter_criteria = buildFilterCriteria();

    const params = {
      filter_criteria,
      sort_order: 'descending',
      limit: 1000,
    };

    if (selectedLocationId && selectedLocationId !== 'all') {
      params.location_id = selectedLocationId;
    } else if (selectedLocationId === 'all' && groupId) {
      params.group_id = groupId;
    }

    fetchCalls(params);
  }, [buildFilterCriteria, selectedLocationId, groupId, fetchCalls]);

  /**
   * Get date range timestamps from preset
   */
  const getDateRangeFromPreset = (preset) => {
    const now = Date.now();
    let start, end = now;

    switch (preset) {
      case 'today':
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        start = today.getTime();
        break;
      case 'last_7_days':
        start = now - 7 * 24 * 60 * 60 * 1000;
        break;
      case 'last_4_weeks':
        start = now - 28 * 24 * 60 * 60 * 1000;
        break;
      case 'last_3_months':
        start = now - 90 * 24 * 60 * 60 * 1000;
        break;
      case 'week_to_date':
        const weekStart = new Date();
        weekStart.setDate(weekStart.getDate() - weekStart.getDay());
        weekStart.setHours(0, 0, 0, 0);
        start = weekStart.getTime();
        break;
      case 'month_to_date':
        const monthStart = new Date();
        monthStart.setDate(1);
        monthStart.setHours(0, 0, 0, 0);
        start = monthStart.getTime();
        break;
      case 'year_to_date':
        const yearStart = new Date();
        yearStart.setMonth(0, 1);
        yearStart.setHours(0, 0, 0, 0);
        start = yearStart.getTime();
        break;
      default:
        return null;
    }

    return { start, end };
  };

  /**
   * Handle date range change
   */
  const handleDateRangeChange = (start, end, preset) => {
    setDateRange({ start, end, preset });
  };

  /**
   * Handle sentiment filter toggle
   */
  const toggleSentimentFilter = (sentiment) => {
    setSentimentFilter(prev => 
      prev.includes(sentiment)
        ? prev.filter(s => s !== sentiment)
        : [...prev, sentiment]
    );
  };

  /**
   * Handle success filter change
   */
  const handleSuccessFilterChange = (value) => {
    setSuccessFilter(successFilter === value ? null : value);
  };

  /**
   * Reset all filters
   */
  const handleResetFilters = () => {
    setDateRange({ start: null, end: null, preset: 'all_time' });
    setSentimentFilter([]);
    setSuccessFilter(null);
  };

  /**
   * Close date picker when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowDatePicker(false);
      }
    };

    if (showDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDatePicker]);

  /**
   * Get date range label for button
   */
  const getDateRangeLabel = () => {
    if (dateRange.start && dateRange.end) {
      return `${dateRange.start.toLocaleDateString()} - ${dateRange.end.toLocaleDateString()}`;
    }
    
    const labels = {
      today: 'Today',
      last_7_days: 'Last 7 days',
      last_4_weeks: 'Last 4 weeks',
      last_3_months: 'Last 3 months',
      week_to_date: 'Week to date',
      month_to_date: 'Month to date',
      year_to_date: 'Year to date',
      all_time: 'All time',
    };
    
    return labels[dateRange.preset] || 'Date Range';
  };

  /**
   * Export calls to PDF
   */
  const handleExportPDF = () => {
    // Create PDF content
    const content = `
YAPN DASHBOARD - CALL RECORDS REPORT
Generated: ${formatDate(Date.now())}
Total Calls: ${calls.length}

${'='.repeat(80)}

${calls.map((call, index) => `
Call #${index + 1}
${'─'.repeat(80)}
Time: ${formatDate(call.start_timestamp)}
Duration: ${call.duration_ms ? Math.floor(call.duration_ms / 1000) + 's' : 'N/A'}
Type: ${call.call_type || 'N/A'}
Status: ${call.call_status || 'N/A'}
Sentiment: ${call.call_analysis?.user_sentiment || 'Unknown'}
Success: ${call.call_analysis?.call_successful ? 'Yes' : 'No'}
End Reason: ${call.disconnection_reason || 'N/A'}

`).join('\n')}

${'='.repeat(80)}
End of Report
    `.trim();

    // Create blob and download
    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `yapn-calls-report-${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl text-white mb-2">
                Call Records
              </h1>
              <p className="text-gray-400">
                Total Sessions: {calls.length.toLocaleString()}
              </p>
            </div>

            {/* Export Button */}
            <Button variant="outline" onClick={handleExportPDF}>
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export PDF
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-6">
          {/* Filters Bar */}
          <div className="flex items-center space-x-3 mb-6">
            {/* Date Range Button */}
            <div className="relative" ref={datePickerRef}>
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="inline-flex items-center px-4 py-2 border border-gray-700 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {getDateRangeLabel()}
              </button>

              {/* Date Picker Popup */}
              {showDatePicker && (
                <DateRangePicker
                  startDate={dateRange.start}
                  endDate={dateRange.end}
                  onChange={handleDateRangeChange}
                  onClose={() => setShowDatePicker(false)}
                />
              )}
            </div>

            {/* Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-700 rounded-lg bg-gray-800 text-sm font-medium text-white hover:bg-gray-700 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filter
                {(sentimentFilter.length > 0 || successFilter !== null) && (
                  <span className="ml-2 px-2 py-0.5 bg-primary-100 text-primary-700 text-xs rounded-full">
                    {sentimentFilter.length + (successFilter !== null ? 1 : 0)}
                  </span>
                )}
                <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showFilters && (
                <div className="absolute top-full mt-2 left-0 w-80 bg-gray-800 rounded-lg shadow-lg border border-gray-700 z-50">
                  <div className="p-4 space-y-4">
                    {/* Sentiment Filter */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                        User Sentiment
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {Object.values(USER_SENTIMENT).map((sentiment) => (
                          <button
                            key={sentiment}
                            onClick={() => toggleSentimentFilter(sentiment)}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                              sentimentFilter.includes(sentiment)
                                ? 'bg-primary-500 text-white border-primary-500'
                                : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                            }`}
                          >
                            {sentiment}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Success Filter */}
                    <div>
                      <label className="block text-xs font-semibold text-gray-300 uppercase tracking-wider mb-2">
                        Call Success
                      </label>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleSuccessFilterChange(true)}
                          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                            successFilter === true
                              ? 'bg-success-500 text-white border-success-500'
                              : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                          }`}
                        >
                          Successful
                        </button>
                        <button
                          onClick={() => handleSuccessFilterChange(false)}
                          className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md border transition-colors ${
                            successFilter === false
                              ? 'bg-error-500 text-white border-error-500'
                              : 'bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600'
                          }`}
                        >
                          Failed
                        </button>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-2 border-t border-gray-700">
                      <button
                        onClick={handleResetFilters}
                        className="text-xs text-gray-400 hover:text-white font-medium"
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => setShowFilters(false)}
                        className="px-3 py-1.5 bg-primary-500 text-white text-xs font-medium rounded-md hover:bg-primary-600 transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Error Message */}
          {error && calls.length === 0 && !isLoading && (
            <Card className="mb-6 border-error-500 bg-error-500/10 p-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-error-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-white">
                    Unable to load calls
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Please check your internet connection and try again
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Calls Table */}
          <CallsTable calls={calls} isLoading={isLoading} />

          {/* Load More Button */}
          {hasMore && calls.length > 0 && !error && (
            <div className="mt-6 flex justify-center">
              <Button
                variant="secondary"
                onClick={loadMore}
                loading={isLoading}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallsPage;
